import React from 'react';
import { formatDateOnly, formatLocalDate, parseLocalDate } from '../utils/dateUtils';

const ClientTimeline = ({ meetings, onViewSummary }) => {
  // Ordenar las juntas de más recientes a más antiguas
  const sortedMeetings = [...meetings].sort((a, b) => {
    // Usar Date directamente para evitar problemas con parseLocalDate
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA; // Orden descendente (más reciente primero)
  });

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-[#0061A8] mb-4">Historial de Juntas</h3>
      <div className="space-y-6">
        {sortedMeetings.map((meeting, index) => {
          const contacts = meeting.contactsGenerated;
          const patients = meeting.patients;
          const conversion = contacts > 0 ? ((patients / contacts) * 100).toFixed(1) + '%' : '0%';

          // Calcular el máximo para escalar las barras de rendimiento por junta
          const maxContactsPatients = Math.max(contacts, patients);
          const contactsWidth = maxContactsPatients > 0 ? (contacts / maxContactsPatients) * 100 : 0;
          const patientsWidth = maxContactsPatients > 0 ? (patients / maxContactsPatients) * 100 : 0;
          const conversionWidth = parseFloat(conversion) > 0 ? parseFloat(conversion) : 0; // Max 100%

          const totalObjections = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);
          const totalServicesValue = (meeting.servicesSold || []).reduce((sum, service) => sum + (service.quantity * parseFloat(service.value || 0)), 0);

          return (
            <div key={meeting.id} className="relative pl-6 border-l-2 border-[#D1D3D4]">
              <div className="absolute w-4 h-4 bg-[#D1D3D4] rounded-full -left-2 top-1"></div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-[#0061A8]">
                    Junta #{sortedMeetings.length - index} - {formatLocalDate(meeting.date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h4>
                  <span className="text-sm text-[#63666A]">
                    {formatDateOnly(meeting.date)}
                  </span>
                </div>
                
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#63666A]">Contactos:</span> {contacts}
                  </div>
                  <div>
                    <span className="text-[#63666A]">Pacientes:</span> {patients}
                  </div>
                </div>
                
                {meeting.nextMeetingDate && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Próxima junta:</span> {formatDateOnly(meeting.nextMeetingDate)}
                    {meeting.nextMeetingTime && ` a las ${meeting.nextMeetingTime} hrs`}
                  </div>
                )}
                
                {meeting.clientMeetingEmail && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Email agendamiento:</span> {meeting.clientMeetingEmail}
                  </div>
                )}

                {meeting.meetingType && (
                  <div className="mt-2 text-sm">
                    <span className="text-[#63666A]">Tipo de Junta:</span> {meeting.meetingType}
                  </div>
                )}

                <div className="mt-3">
                  <h5 className="text-sm font-medium text-[#63666A]">Resumen:</h5>
                  <p className="text-sm text-[#63666A] whitespace-pre-line">{meeting.observations}</p>
                </div>
                
                <div className="mt-2">
                  <h5 className="text-sm font-medium text-[#63666A]">Acciones:</h5>
                  <p className="text-sm text-[#63666A] whitespace-pre-line">{meeting.agreements}</p>
                </div>

                {totalObjections > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-[#63666A]">Objeciones:</h5>
                    <ul className="list-disc list-inside text-sm text-[#63666A]">
                      {meeting.objections.price > 0 && <li>Precio: {meeting.objections.price} personas</li>}
                      {meeting.objections.location > 0 && <li>Ubicación: {meeting.objections.location} personas</li>}
                      {meeting.objections.noResponse > 0 && <li>No contestan 1er mensaje: {meeting.objections.noResponse} personas</li>}
                      {meeting.objections.schedule > 0 && <li>Horarios: {meeting.objections.schedule} personas</li>}
                      {meeting.objections.other && <li>Otras: {meeting.objections.other}</li>}
                    </ul>
                  </div>
                )}

                {totalServicesValue > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-[#63666A]">Servicios Vendidos:</h5>
                    <ul className="list-disc list-inside text-sm text-[#63666A]">
                      {(meeting.servicesSold || []).map((service, sIndex) => service.name && (
                        <li key={sIndex}>{service.name}: {service.quantity} unidades, Valor: ${parseFloat(service.value || 0).toFixed(2)} c/u (Total: ${(service.quantity * parseFloat(service.value || 0)).toFixed(2)})</li>
                      ))}
                    </ul>
                    <p className="text-sm font-bold text-[#0061A8] mt-1">Valor total: ${totalServicesValue.toFixed(2)}</p>
                  </div>
                )}

                {/* Barras de rendimiento por junta */}
                <div className="mt-4 space-y-2">
                  <h6 className="text-sm font-semibold text-[#0061A8]">Rendimiento de esta Junta</h6>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Contactos: {contacts}</span>
                      <span>{contacts}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${contactsWidth}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Pacientes: {patients}</span>
                      <span>{patients}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: `${patientsWidth}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-[#63666A]">
                      <span>Tasa de Conversión: {conversion}</span>
                      <span>{conversion}</span>
                    </div>
                    <div className="w-full bg-[#D1D3D4] rounded-full h-2">
                      <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${conversionWidth}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                  {meeting.googleCalendarLink && (
                    <a
                      href={meeting.googleCalendarLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm bg-[#A8D08D] text-[#FFFFFF] py-1 px-3 rounded-md hover:bg-opacity-90 transition-colors flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 12h-2v2h2v-2zm-4 0h-2v2h2v-2zm-4 0H7v2h2v-2zm10-9h-1V2h-2v1H8V2H6v1H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
                      </svg>
                      Agendar
                    </a>
                  )}
                  <button
                    onClick={() => onViewSummary(meeting)}
                    className="text-sm bg-[#0061A8] text-[#FFFFFF] py-1 px-3 rounded-md hover:bg-opacity-90 transition-colors"
                  >
                    Ver Minuta
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientTimeline;
